import { useMemo } from 'react';
import type { TravelItemIdsFragment } from '../../../apps/admin/routes/WebsiteEditor/features/travel/WebsiteEditorTravel.graphql.generated';
import type { EventAccommodationPublicDataFragment, EventAccommodationsCheckQuery } from './useEventAccommodationsCheck.graphql.generated';
import type { EventPageVisibility } from '@graphql/generated-base';
import { useEventAccommodationsCheckQuery } from './useEventAccommodationsCheck.graphql.generated';
import { AccommodationPlaceRoomBlockStatus, AccommodationPlaceType, EventPlaceType } from '@graphql/generated-base';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { sortByOrderList } from '@shared/utils/array';
import { useFeatureValue } from '@shared/core/featureFlags';
import { isEventPageVisible } from '@shared/utils/pageVisibility';

const CUSTOM_ACCOMMODATIONS_PAGE_SLUG = 'accommodations';
const TRAVEL_EVENT_PAGE_SLUG = 'travel';
const ACCOMMODATION_TRAVEL_TYPE_SET = new Set<TravelItemIdsFragment['__typename']>(['Hotel', 'RentalHome', 'Tent']);

type UseEventAccommodationsCheckArgs = {
  eventHandle: string;
  /**
   * Skip the underlying GraphQL query. Use this when `eventHandle` may be empty/unresolved
   * to avoid firing a guaranteed-to-fail request. When skipped, `isResolved` returns `false`
   * until the caller provides a real handle and toggles `skip` to `false`.
   */
  skip?: boolean;
  ssr?: boolean;
};

export type UseEventAccommodationsCheckReturn = {
  /**
   * Whether the core accommodations data query has resolved (GQL only).
   * Use this when you only need `hasAccommodationListings` / `hasTravelAccommodationListings`
   * and don't care about `matchedCustomPageSlug` (which depends on a remote config fetch).
   */
  isAccommodationsDataResolved: boolean;
  /**
   * Whether **all** hook work has resolved, including the remote-config fetch for
   * `accommodationsCustomPageSlugKeywords`. Gate on this when you need `matchedCustomPageSlug`.
   */
  isResolved: boolean;

  eventLocationPlaceId: Maybe<string>;
  eventLocationCityName: Maybe<string>;
  eventDisplayName: Maybe<string>;
  eventFinalizedDate: Maybe<{ milliseconds: number; timezone: string }>;

  ////////////////////////////////////////////////////////////
  // Accommodations page related data
  ////////////////////////////////////////////////////////////
  accommodationListings: EventAccommodationData[];
  /**
   * Whether the event has accommodation listings (eventByName.accommodationPlacesV2).
   */
  hasAccommodationListings: boolean;
  /**
   * Whether the event has a visible accommodations event page.
   *
   * - **visible** = the page is either public or password protected.
   */
  hasVisibleAccommodationsEventPage: boolean;

  ////////////////////////////////////////////////////////////
  // Travel page related data
  ////////////////////////////////////////////////////////////

  /**
   * Whether the event has travel accommodation listings (eventByName.info.travel).
   *
   * - **visible** = the page is either public or password protected.
   * - an accommodation travel listing is a travel item that is either a Hotel, RentalHome, or Tent.
   */
  hasTravelAccommodationListings: boolean;
  /**
   * Whether the event has a visible travel event page.
   */
  hasVisibleTravelEventPage: boolean;

  error: Error | undefined;
  /**
   * The `pageSlug` of the first public custom page that matches known
   * accommodation-related keywords (e.g., "travel", "hotel", "accommodation").
   */
  matchedCustomPageSlug?: string;
};

const isPageInfoVisible = (pageInfo?: { visibility: EventPageVisibility | 'hidden' | 'passwordProtected' | 'public' | 'specificGuests' }) => {
  return isEventPageVisible(pageInfo?.visibility);
};

const isAnAccommodationLikeTravelListing = (travelListing: NonNullable<EventAccommodationsCheckQuery['eventByName']>['info']['travel'][number]) => {
  return travelListing.__typename ? ACCOMMODATION_TRAVEL_TYPE_SET.has(travelListing.__typename) : false;
};

export enum AccommodationPlaceTypeExtended {
  custom = 'custom',
  hotel = 'hotel',
  roomBlock = 'roomBlock',
}
export interface ExtendedEventAccommodationPublicData extends Omit<EventAccommodationPublicDataFragment, 'type'> {
  type: AccommodationPlaceTypeExtended;
  distanceFromVenue?: number | null;
}
export type EventAccommodationData = ExtendedEventAccommodationPublicData;

export const useEventAccommodationsCheck = (args: UseEventAccommodationsCheckArgs): UseEventAccommodationsCheckReturn => {
  const { eventHandle, skip: skipArg, ssr } = args;
  // Skip the query when the caller opts out explicitly or when no eventHandle is
  // available yet — firing with `name: ''` produces a guaranteed-invalid request.
  const skip = skipArg || !eventHandle;
  const { data, loading, error } = useEventAccommodationsCheckQuery({
    variables: {
      name: eventHandle,
      pageContainerBySlugPayload: {
        eventName: eventHandle,
        pageSlug: 'accommodations',
      },
    },
    batchMode: 'off',
    fetchPolicy: 'cache-first',
    skip,
    ...(ssr === false && { ssr: false }),
  });
  const processedAccommodationsData = useMemo(() => {
    if (!data?.eventByName) return { accommodationListings: [], hasAccommodationListings: false, hasVisibleAccommodationsEventPage: false };

    const { eventByName, pageContainerBySlug } = data;
    const { places = [], accommodationPlacesV2 = [], info, pagesInfo = [] } = eventByName;

    const venue = places.find(place => place.type === EventPlaceType.venue) || info?.locationInfo;
    const accommodations = accommodationPlacesV2.filter(({ accommodationRoomBlock }) => accommodationRoomBlock?.status !== AccommodationPlaceRoomBlockStatus.cancelled);

    const travelMapComponent = pageContainerBySlug?.components?.find(comp => comp.__typename === 'TravelMapComponent');
    const ordering = travelMapComponent?.__typename === 'TravelMapComponent' ? travelMapComponent?.blockOrdering : [];
    const sortedAccommodations = ordering?.length ? sortByOrderList(accommodations, ordering) : accommodations;

    const pageInfo = pagesInfo?.find(p => p.page.pageSlug === CUSTOM_ACCOMMODATIONS_PAGE_SLUG);

    const accommodationListings = sortedAccommodations.map<EventAccommodationData>(accommodation => {
      let type = accommodation.type === AccommodationPlaceType.hotel ? AccommodationPlaceTypeExtended.hotel : AccommodationPlaceTypeExtended.custom;
      if (accommodation.accommodationRoomBlock?.isJoyManagedRoomBlock) {
        type = AccommodationPlaceTypeExtended.roomBlock;
      }
      const accommodationExtended = { ...accommodation, type };
      const distance = distanceInMiles(venue, accommodation);

      const distanceFromVenue = distance;

      return {
        ...accommodationExtended,
        distanceFromVenue,
      };
    });

    return {
      accommodationListings,
      hasAccommodationListings: accommodationListings.length > 0,
      hasVisibleAccommodationsEventPage: isPageInfoVisible(pageInfo),
    };
  }, [data]);

  const processedTravelData = useMemo(() => {
    const hasTravelAccommodationListings = (data?.eventByName?.info.travel?.filter(isAnAccommodationLikeTravelListing).length ?? 0) > 0;
    const travelEventPageInfo = data?.eventByName?.pagesInfo?.find(pageInfo => pageInfo.page.pageSlug === TRAVEL_EVENT_PAGE_SLUG);

    return {
      hasTravelAccommodationListings,
      hasVisibleTravelEventPage: isPageInfoVisible(travelEventPageInfo),
    };
  }, [data]);
  const eventInfo = data?.eventByName?.info;

  // Get list of all accommodations related keywords from amplitude, find first visible matching page slug from custom pages.
  // A caller that skips the hook reads nothing, this flag included.
  const { loading: loadingSearchKeywords, payload } = useFeatureValue('accommodationsCustomPageSlugKeywords', {
    skip:
      !!skipArg ||
      processedAccommodationsData.hasVisibleAccommodationsEventPage ||
      (processedTravelData.hasVisibleTravelEventPage && processedTravelData.hasTravelAccommodationListings),
  });
  const accommodationsCustomPageSlugKeywords = payload as string[] | null;
  // Assume config keywords are lowercased already
  const normalizedKeywords = useMemo(() => {
    return (accommodationsCustomPageSlugKeywords ?? [])
      .map(k => {
        if (k.startsWith('/') && k.endsWith('/')) {
          try {
            const pattern = k.slice(1, -1);
            return new RegExp(pattern);
          } catch (e) {
            return null;
          }
        }
        return k;
      })
      .filter(Boolean);
  }, [accommodationsCustomPageSlugKeywords]);
  // Find matching custom page slug
  const matchedCustomPageSlug = useMemo(() => {
    const matchedCustomPage = data?.eventByName?.pagesInfo?.find(pageInfo => {
      // Skip already checked accommodations and travel pages
      if (pageInfo.page.pageSlug === TRAVEL_EVENT_PAGE_SLUG) return false;
      // Skip if page is not visible
      if (!isPageInfoVisible(pageInfo)) return false;
      const slug = pageInfo.page.pageSlug.toLowerCase();

      return normalizedKeywords.some(keyword => {
        if (typeof keyword === 'string') {
          return slug.includes(keyword);
        } else if (keyword instanceof RegExp) {
          return keyword.test(slug);
        }
        return false;
      });
    });

    return matchedCustomPage?.page?.pageSlug;
  }, [data?.eventByName?.pagesInfo, normalizedKeywords]);

  // When the query is skipped, Apollo reports `loading: false` immediately, and on the render where `skip`
  // flips off it can report `loading: false` before the query has answered. Only data or an error counts as
  // resolved, so callers never act on the empty data of a skipped or not-yet-answered query.
  const hasQueryAnswered = !skip && !loading && (!!data || !!error);

  return {
    isAccommodationsDataResolved: hasQueryAnswered,
    isResolved: hasQueryAnswered && !loadingSearchKeywords,
    eventDisplayName: eventInfo?.eventDisplayName,
    eventLocationPlaceId: eventInfo?.placeId,
    eventFinalizedDate: eventInfo?.finalizedDate,
    eventLocationCityName: eventInfo?.locationInfo?.city,
    error,
    ...processedAccommodationsData,
    ...processedTravelData,
    matchedCustomPageSlug,
  };
};
